import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  Fragment,
  useMemo,
} from 'react';
import {
  downloadStudio,
  getStudioBinaryType,
  getStudioProtocolURL,
  openStudioProtocol,
} from '@modules/ui/shared/utils/studioHelper';
import { Locale, useLocalization, useTranslation } from '@rbx/intl';
import { Button, Dialog, Grid, Typography, LinearProgress, IconButton, CloseIcon } from '@rbx/ui';
import StudioLogo from '@modules/miscellaneous/common/components/StudioLogo';
import clientSettingsClient from '@modules/clients/clientSettingsClient';
import useTryInStudioDialogStyles from './TryInStudioDialog.styles';

enum State {
  Launching,
  Error,
  Done,
}

export type TryInStudioDialogProps = {
  assetId?: number;
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  checkForStudioTimeout?: number;
  onDownloadClicked?: () => void;
};

const TryInStudioDialog: FunctionComponent<React.PropsWithChildren<TryInStudioDialogProps>> = ({
  assetId,
  userId,
  isOpen,
  onClose,
  checkForStudioTimeout,
  onDownloadClicked: onDownloadClickedCb,
}) => {
  const { translate } = useTranslation();
  const { locale } = useLocalization();

  const {
    classes: { container, loadingContainer, closeButton },
  } = useTryInStudioDialogStyles();
  const [state, setState] = useState(State.Launching);
  const loading = state === State.Launching;

  const fetchChannel = async () => {
    const channelName = await clientSettingsClient.getUserChannel(getStudioBinaryType());
    return channelName?.toUpperCase() !== 'LIVE' ? channelName : '';
  };

  const onDownloadClicked = useCallback(() => {
    if (onDownloadClickedCb) onDownloadClickedCb();
    downloadStudio();
    onClose();
  }, [onClose, onDownloadClickedCb]);

  const actionButton = useMemo(() => {
    if (state === State.Done) {
      return (
        <Button
          aria-label='download'
          data-testid='download-studio-button'
          onClick={onDownloadClicked}>
          {translate('Action.DownloadStudio')}
        </Button>
      );
    }
    if (state === State.Error) {
      return (
        <Button aria-label='close' data-testid='close-error-button' onClick={onClose}>
          {translate('Action.Cancel')}
        </Button>
      );
    }
    return null;
  }, [onClose, onDownloadClicked, state, translate]);

  const actionMessage = useMemo(() => {
    if (state === State.Done) {
      return translate('Label.GetStartedWithStudio');
    }
    if (state === State.Error) {
      return translate('Error.UnexpectedError');
    }
    return translate('Label.CheckingForStudio');
  }, [state, translate]);

  useEffect(() => {
    let isMounted = true;
    let timeout: NodeJS.Timeout;

    const cleanup = () => {
      isMounted = false;
      clearTimeout(timeout);
    };

    if (!userId || !isMounted || !isOpen) {
      return cleanup;
    }

    (async () => {
      if (!isMounted) {
        return;
      }

      try {
        const channel = await fetchChannel();
        const url = getStudioProtocolURL({
          userId: userId.toString(),
          locale: locale || Locale.English,
          channel: channel || '',
          assetId: assetId ? assetId.toString() : '',
        });

        openStudioProtocol(url);
        timeout = setTimeout(
          () => isMounted && setState(State.Done),
          checkForStudioTimeout || 5000,
        );
      } catch (e) {
        if (isMounted) {
          setState(State.Error);
        }
      }
    })();

    return cleanup;
  }, [assetId, checkForStudioTimeout, isOpen, locale, userId]);

  return (
    <Dialog open={isOpen} maxWidth='Large'>
      <Grid container className={container} align-items='center' justifyContent='center'>
        <Grid
          item
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
          spacing={2}
          XSmall={11}
          data-testid='tryInStudioDialog'>
          <Grid item>
            <StudioLogo width={120} height={120} />
          </Grid>

          <Grid item>
            <Typography paragraph data-testid='status-text' align='center'>
              {actionMessage}
            </Typography>
          </Grid>

          {loading && (
            <Grid item className={loadingContainer}>
              <LinearProgress title='loading' variant='indeterminate' />
            </Grid>
          )}
          {actionButton}
        </Grid>
      </Grid>
      <IconButton size='medium' className={closeButton} aria-label='close' onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};

export default TryInStudioDialog;
